import { Box, Card, CardActions, CardContent, Grid, Link, Stack, Typography } from '@mui/material';
import { AppImageDimension } from 'app/appImages';
import { MprcFormBarHeader, MprcInfiniteLoading, MprcLink, MprcRequestErrorReport, MprcRequestIndicator } from 'components';
import { ButtonBack } from 'components/buttons';
import { CardAction, CardActionLocation, CardActionType, CardDto, CardThumbnailDisplayMode, CarouselCardRenderMode, CarouselLinkInfoDto, ChooseLandingPageOptions, SystemFeature } from "generated/CoreApi_v1";
import useDidUpdateEffect from 'hooks/useNoInitialEffect';
import { OUTSIDE_LAYOUT_CONTENT_ID, OUTSIDE_LAYOUT_PORTAL_CONTAINER_ID } from 'layouts/common/outside/MprcLayoutOutside';
import HtmlUtils from 'lib/HtmlUtils';
import { useSnackbar } from 'notistack';
import { useCoreApi } from 'providers/CoreApiProvider';
import React, { useEffect, useRef, useState } from 'react';
import ReactDOM from 'react-dom';
import { useNavigate } from 'react-router';
import { CardDtoActions, CardRenderActions, resolveLinkFeature } from 'views/general/Cards/CardDtoActions';
import { CardThumbnail, CardThumbnailAvatar, discoverPath, IBuildCardProps, ICardDtoActionsProps, ICardDtoToCardRibbonOptions, RenderCardSticker, RenderCardText } from 'views/general/Cards/Common';

///////////////////////////////////////////////////////////////////////////////


const Body = (cardActions: ICardDtoActionsProps) => {

    const item = cardActions.item;

    const primaryAction = item.actions.find(p => p.location == CardActionLocation.Primary);
    const [action, setAction] = useState<CardAction | null>();

    return (
        <>
            {action && <CardRenderActions {...cardActions} cardAction={action} endAction={() => setAction(null)} />}
            <MprcLink onClick={primaryAction ? () => setAction(primaryAction) : undefined}>
                <Box height={"74px"}>
                    {
                        item.lines.map((line, index) => <RenderCardText key={index} line={line} />)
                    }
                </Box>
            </MprcLink>
        </>
    );
};

///////////////////////////////////////////////////////////////////////////////

const BuildThumbnail = ({ item, saveState }: { item: CardDto; saveState?: any; }) => {
    const [action, setAction] = useState<CardAction | null>();

    if (item.thumbnail == null) return null;

    const thumbnailAction = item.actions.find(p => p.location == CardActionLocation.Thumbnail);
    const thumbnailLink = thumbnailAction && thumbnailAction.notConvertActionToLink === false ? discoverPath(thumbnailAction, item.data)?.url : undefined;

    return item.thumbnail.displayMode == CardThumbnailDisplayMode.Avatar
        ? <CardThumbnailAvatar thumbnailUrl={item.thumbnail.url} thumbnailLink={thumbnailLink} saveState={saveState} />
        : thumbnailLink != undefined
            ? <CardThumbnail thumbnailUrl={item.thumbnail.url} thumbnailLink={thumbnailLink} saveState={saveState} />
            : thumbnailAction?.notConvertActionToLink === true
                ? (
                    <>
                        {action && <CardRenderActions item={item} cardAction={action} endAction={() => setAction(null)} />}
                        <CardThumbnail thumbnailUrl={item.thumbnail.url} onClick={() => setAction(thumbnailAction)} />
                    </>
                )
                : <CardThumbnail thumbnailUrl={item.thumbnail.url} />;
};

///////////////////////////////////////////////////////////////////////////////

const RenderCardMedia = (cardActions: ICardDtoActionsProps) => {

    const item = cardActions.item;
    const saveState = cardActions.saveState;
    const thumbnailAction = item.actions.find(p => p.location == CardActionLocation.Thumbnail);
    const thumbnailLink = thumbnailAction ? discoverPath(thumbnailAction, item.data)?.url : undefined;
    const primaryAction = item.actions.find(p => p.location == CardActionLocation.Primary);

    const [action, setAction] = useState<CardAction | null>();

    return (
        <>
            {action && <CardRenderActions {...cardActions} cardAction={action} endAction={() => setAction(null)} />}
            <MprcLink onClick={primaryAction && thumbnailAction == undefined && thumbnailLink == undefined ? () => setAction(primaryAction) : undefined}>
                {
                    item.stickerLeft || item.stickerRight
                        ? <Box sx={{ position: "relative" }}>
                            <BuildThumbnail item={item} saveState={saveState} />
                            <Box sx={{ position: "absolute", bottom: 0, left: 0, width: "100%" }}>
                                <Grid container justifyContent="center" width="100%">
                                    <Grid size={{ xs: 6 }}>{item.stickerLeft ? <RenderCardSticker item={item.stickerLeft} /> : null}</Grid>
                                    <Grid size={{ xs: 6 }}>{item.stickerRight ? <RenderCardSticker item={item.stickerRight} /> : null}</Grid>
                                </Grid>
                            </Box>
                        </Box>
                        : <BuildThumbnail item={item} saveState={saveState} />
                }
            </MprcLink>
        </>
    );
};

///////////////////////////////////////////////////////////////////////////////

const BuildDefaultCard = ({ item, saveState, ...rest }: IBuildCardProps) => {
    return (
        <Card
            key={`${item.uniqueId}-default`}
            sx={{
                minWidth: AppImageDimension.ThumbnailWide.width,
                maxWidth: AppImageDimension.ThumbnailWide.width,
                boxShadow: "rgba(0,0,0,0.1) 2px 2px 8px 2px",
                mt: "6px",
                mr: "8px",
            }}
            elevation={0}
        >
            <RenderCardMedia item={item} saveState={saveState} {...rest} />
            <CardContent sx={{ p: 1 }}>
                <Body item={item} saveState={saveState} {...rest} />
            </CardContent>
            <CardActions disableSpacing sx={{ p: 0, pl: 1, pr: 1 }}>
                <CardDtoActions item={item} saveState={saveState} {...rest} />
            </CardActions>
        </Card>
    );
};

///////////////////////////////////////////////////////////////////////////////
interface IRenderIframeProps {
    carouselLinkId: string;
    containerElement: Element;
    onClose: () => void;
}

///
const RenderIFrame = (props: IRenderIframeProps) => {
    const api = useCoreApi();
    const [loading, setLoading] = useState<Boolean>(true);
    const [viewData, setViewData] = useState<CarouselLinkInfoDto>(null!);
    const [viewError, setViewError] = useState<any>(null);

    ///
    const loadContent = async () => {
        try {
            let result = await api.v1.carousel.getCarouselLinkInfo({ carouselLinkId: props.carouselLinkId });
            setViewData(result.data);
        } catch (e: any) {
            setViewError(e as Error);
        }
        setLoading(false);
    };

    ///
    useEffect(() => {
        loadContent();
    }, []);

    ///
    return ReactDOM.createPortal(
        <Box width="100%" height="100%">
            {
                loading
                    ? <MprcInfiniteLoading visible />
                    : <Stack direction="column" width="100%" height="100%" gap={1}>
                        <Box sx={{ position: "sticky", top: 0 }}>
                            <MprcFormBarHeader
                                actions={
                                    [
                                        <ButtonBack rounded size="large" variant="text" onClick={props.onClose} />,
                                        <Stack direction="column" alignItems="end" justifyContent="center">
                                            <Typography variant="h4" overflow="hidden" textOverflow="ellipsis" whiteSpace="nowrap">{viewData.title}</Typography>
                                            {viewData.subtitle &&
                                                <Typography variant="caption" overflow="hidden" textOverflow="ellipsis" whiteSpace="nowrap">{viewData.subtitle}</Typography>
                                            }
                                        </Stack>
                                    ]
                                }
                            />
                        </Box>
                        {
                            viewError
                                ? <MprcRequestErrorReport error={viewError} />
                                : <Box alignContent="center" justifyItems="center" flexGrow={1} >
                                    <div
                                        dangerouslySetInnerHTML={
                                            { __html: viewData.iframeContent! }
                                        }
                                    />
                                </Box>
                        }
                    </Stack >
            }
        </Box>
        , props.containerElement);
};

///////////////////////////////////////////////////////////////////////////////

const BuildLinkCard = ({ item, ...rest }: IBuildCardProps) => {
    const navigate = useNavigate();
    const api = useCoreApi();
    const { enqueueSnackbar } = useSnackbar();

    const action = item.actions.find(p => p.location == CardActionLocation.Card);
    const link = action ? discoverPath(action, item.data)?.url ?? "#" : "#";
    const openSameWindow = item.data["openSameWindow"] === true;

    const [loadingPerforma, setLoadingPerforma] = useState<boolean>(false);

    const [dialog, setDialog] = useState<any>(null);
    const [iframeLinkId, setIframeLinkId] = useState<string | null>(null);

    const outsideContainer = useRef<HTMLDivElement>(undefined!);
    const outsidePortalContainer = useRef<HTMLDivElement>(undefined!);

    ///
    const hideContent = () => {
        outsideContainer.current.classList.add("hide");
        outsidePortalContainer.current.classList.add("show");
    };

    ///
    const undoHide = () => {
        outsideContainer.current.classList.remove("hide");
        outsidePortalContainer.current.classList.remove("show");
    };

    //
    const loadPerformaManage = async (landingPage: ChooseLandingPageOptions) => {
        setLoadingPerforma(true);
        try {
            var response = await api.v1.performaIntegration.chooseLandingpage({ landingPage });
            HtmlUtils.openUrlInNewWindow(response.data.url, ChooseLandingPageOptions[landingPage]);
        }
        catch {
            enqueueSnackbar("Não foi possível determinar a URL de destino", { variant: "error" });
        }
        finally {
            setLoadingPerforma(false);
        }
    };

    ///
    useEffect(() => {
        outsideContainer.current = document.getElementById(OUTSIDE_LAYOUT_CONTENT_ID) as HTMLDivElement;
        outsidePortalContainer.current = document.getElementById(OUTSIDE_LAYOUT_PORTAL_CONTAINER_ID) as HTMLDivElement;
        return () => {
            // Restaura estado inicial
            undoHide();
        };
    }, []);

    ///
    useDidUpdateEffect(() => {
        if (iframeLinkId != null) hideContent();
        else undoHide();
    }, [iframeLinkId]);

    ///
    return (
        <Card
            key={`${item.uniqueId}-link`}
            sx={{
                width: 294,
                boxShadow: "rgba(0,0,0,0.1) 2px 2px 8px 2px",
                mt: "6px",
                mr: `8px`,
            }}
            elevation={0}
        >
            {loadingPerforma && <MprcRequestIndicator type='waiting' />}

            {
                iframeLinkId != null &&
                <RenderIFrame
                    carouselLinkId={iframeLinkId}
                    containerElement={outsidePortalContainer.current}
                    onClose={() => setIframeLinkId(null)}
                />
            }

            <Box sx={{ p: 1 }}>
                <Link
                    href={action?.actionType == CardActionType.ExternalLink && openSameWindow ? link : "#"}
                    onClick={
                        (e) => {
                            if (action?.actionType == CardActionType.ExternalLink && !openSameWindow) {
                                e.preventDefault();
                                HtmlUtils.openUrlInNewWindow(link, "external-link", { noopener: true, noreferrer: true });
                            }
                            if (action?.actionType == CardActionType.IFrameContent) {
                                e.preventDefault();
                                setIframeLinkId(action.carouselLinkId);
                            }
                            else if (action?.actionType == CardActionType.LinkFeature && action.linkFeature !== null) {
                                e.preventDefault();
                                if (action.linkFeature == SystemFeature.PerformaManageAd)
                                    loadPerformaManage(ChooseLandingPageOptions.ManageAD);
                                else if (action.linkFeature == SystemFeature.PerformaManageGoal)
                                    loadPerformaManage(ChooseLandingPageOptions.ManageGoal);
                                else
                                    resolveLinkFeature(action.linkFeature, setDialog, navigate);
                            }
                            else enqueueSnackbar("Ação não definida", { variant: "error" });
                        }
                    }
                >
                    <Stack direction="row" spacing={1}>
                        {
                            item.thumbnail &&
                            <Box sx={{ maxWidth: "48px" }}>
                                <img src={item.thumbnail.url} style={{ width: "100%", objectFit: "contain" }} />
                            </Box>
                        }
                        <Body item={item} {...rest} />
                    </Stack>
                </Link>
                {dialog}
            </Box>
        </Card>
    );
};

///////////////////////////////////////////////////////////////////////////////

export function CardDtoToCardRibbon(items: CardDto[] | undefined, renderMode: CarouselCardRenderMode, options?: ICardDtoToCardRibbonOptions): React.ReactNode[] {
    return items == undefined
        ? []
        : items
            .map((item) => {
                return renderMode == CarouselCardRenderMode.Link
                    ? <BuildLinkCard key={item.uniqueId} item={item} {...options} />
                    : <BuildDefaultCard key={item.uniqueId} item={item} {...options} />;
            });
}
